import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import SectionCard from "./SectionCard";
import { tokens } from "../tokens";

interface Props {
  labels: string[];
  options: string[];
  onChange: (labels: string[]) => void;
  /** Locked while a save is in progress. */
  disabled?: boolean;
}

const LabelsCard = ({ labels, options, onChange, disabled = false }: Props) => (
  <SectionCard title="Labels">
    <Autocomplete
      multiple
      freeSolo
      disabled={disabled}
      filterSelectedOptions
      value={labels}
      options={options}
      onChange={(_, newValue) => onChange(newValue as string[])}
      renderTags={(tagValue, getTagProps) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            width: "100%",
            /* Capped so the chips plus the text input below them still fit
               inside the field's fixed 150px: 12 padding + 96 + ~23 input +
               12 padding. The chips scroll here rather than growing the card. */
            maxHeight: 96,
            overflowY: "auto",
          }}
        >
          {tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option}
                size="small"
                {...tagProps}
                sx={{
                  backgroundColor: tokens.accent,
                  color: "#FFFFFF",
                  fontWeight: 600,
                  "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" },
                }}
              />
            );
          })}
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder="Select Label" />
      )}
      sx={{
        "& .MuiOutlinedInput-root": {
          /* A hard height, not a minimum: with `minHeight` the field still grew
             because the chip container and the text input stack vertically. */
          height: 150,
          alignItems: "flex-start",
          borderRadius: tokens.radius,
          fontSize: 13,
          padding: "12px",
          "& fieldset": { borderColor: tokens.border },
          "&:hover fieldset": { borderColor: tokens.borderStrong },
          "&.Mui-focused fieldset": {
            borderColor: tokens.accent,
            borderWidth: "1px",
          },
        },
        "& .MuiAutocomplete-popupIndicator": { display: "none" },
        /* Pinned near the top of the tall input rather than vertically centred,
           inset far enough to clear the border — MUI's default `right: 9` plus
           the clear button's negative margin put it on the edge — and dropped
           to sit level with the first row of chips. */
        "& .MuiAutocomplete-endAdornment": { top: 14, right: 10 },
        "& .MuiAutocomplete-clearIndicator": { marginRight: 0 },
      }}
    />
  </SectionCard>
);

export default LabelsCard;
